'use client';
import React, { useState } from 'react';
import Head from 'next/head';
import Link from 'next/link';

const sections = [
  {
    title: "Our Refund Policy",
    content:
      "At Vilva Business, we strive to deliver exceptional value through our subscription plans, digital marketing services, and content solutions. We offer refunds under specific conditions to ensure fairness while maintaining operational sustainability. This Refund Policy applies to all payments made on https://www.vilvabusiness.com/.",
  },
  {
    title: "Subscription Plans (Monthly/Annual)",
    content:
      `• Refunds are available only within 7 days of the initial subscription purchase.\n
• No refunds for partial months or early cancellation after 7 days.\n
• Auto-renewal charges are non-refundable unless a technical failure prevented service delivery.\n
• Refunds for annual plans are prorated only if requested within 7 days and no services have been utilized (e.g., no content delivered, no campaigns launched).\n
• To request a subscription refund, contact us at support@vilvabusiness.com with your order ID.`,
  },
  {
    title: "Wallet / Fund Additions (One-Time Credits)",
    content:
      `• Funds added to your Vilva Business wallet for availing extra services (content, ads, designs, etc.) are non-refundable once credited.\n
• Unused wallet balance can be used anytime within 12 months from the date of addition.\n
• No cash refunds for unused credits after 12 months — balance expires automatically.\n
• Exception: Full refund within 24 hours if funds were added by mistake and no services were ordered.`,
  },
  {
    title: "Content & Service Orders (Custom Work)",
    content:
      `• No refunds for completed and delivered content (articles, videos, graphics, ad creatives, etc.).\n
• No refund if client approves draft or provides feedback and work proceeds.\n
• Revisions are free as per package limits. Additional revisions beyond package are chargeable and non-refundable.`,
  },
  {
    title: "Technical Issues & Service Downtime",
    content:
      "If Vilva Business platform is down for more than 48 consecutive hours and you are unable to access paid services, you may request a prorated credit or refund for the affected period. Compensation is limited to the amount paid for the affected billing cycle only.",
  },
  {
    title: "How to Request a Refund",
    content:
      `1. Email info@vilvabusiness.com within the eligible timeframe.\n
2. Include your Order ID, Registered Email, and Reason for Refund.\n
3. Our team will review within 3–5 business days.\n
4. Approved refunds are processed within 7–10 business days via original payment method (or wallet credit if preferred).\n
`,
  },
  {
    title: "Non-Refundable Items",
    content:
      `• Domain registration, hosting, or third-party tool subscriptions purchased through Vilva Business.\n
• Services already delivered or in progress beyond cancellation window.\n
• Charges for payment gateway fees, taxes, or transaction costs.`,
  },
  {
    title: "Changes to This Policy",
    content:
      "Vilva Business reserves the right to update this Refund Policy at any time. Changes will be posted on this page with the updated date. Continued use of our services after changes constitutes acceptance of the new policy.",
  },
];

const RefundPolicy = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggleSection = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <>
      <div className='bg-[linear-gradient(180deg,_#EEF3FF_0%,_#FFF_26.53%)]'>
        <div className="max-w-2xl mx-auto px-4 py-[50px] text-[#1f1f1f]">
          <h1 className="text-[color:var(--Dark-Color,#090D28)] text-center mb-[40px] font-LatinkaBold text-[32px] md:text-[40px] not-italic font-normal leading-[120%]">
            Refund Policy
          </h1>
          <p className="text-[#373844] font-LatinkaRegular mb-[24px] text-[14px] md:text-[16px] not-italic font-normal leading-[150%]">
            Thank you for choosing Vilva Business – Solutions for Success & Growth. We value your trust and are committed to transparency in our refund process.
          </p>

          {sections.map((section, index) => (
            <div
              key={index}
              className={`border-t py-[24px] ${index === sections.length - 1 ? 'border-b' : ''}`}
            >
              <div
                onClick={() => toggleSection(index)}
                className="flex justify-between items-center cursor-pointer"
              >
                <h2 className="text-[color:var(--Dark-Color,#090D28)] font-LatinkaBold text-[20px] md:text-[24px] not-italic font-normal leading-[100%]">
                  {section.title}
                </h2>
                <button className="flex w-[32px] h-[32px] p-[6px] justify-center items-center aspect-square rounded-[6px] bg-[#EFF4FF]">
                  {openIndex === index ? (
                    <svg
                      className={`transform transition-transform duration-300 ${
                        openIndex !== index ? 'rotate-0' : 'rotate-180'
                      }`}
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M4.16602 10H15.8327"
                        stroke="#195AFF"
                        strokeWidth="1.25"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <svg
                      className={`transform transition-transform duration-300 ${
                        openIndex === index ? 'rotate-90' : 'rotate-0'
                      }`}
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M4.16602 10H15.8327"
                        stroke="#195AFF"
                        strokeWidth="1.25"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M10 4.16667V15.8333"
                        stroke="#195AFF"
                        strokeWidth="1.25"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
              </div>

              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  openIndex === index ? 'max-h-96 mt-2' : 'max-h-0'
                }`}
              >
                <div className="text-[#373844] font-LatinkaRegular text-[14px] md:text-[16px] not-italic font-normal leading-[150%] whitespace-pre-wrap">
                  {section.content}
                </div>
              </div>
            </div>
          ))}

          <div className='mt-[80px] flex items-center justify-center gap-[16px] flex-wrap'>
            <Link
              className="text-[color:var(--Main-colour,#195AFF)] font-LatinkaMedium text-[14px] not-italic font-normal leading-[120%] !underline"
              href={'/privacy-policy'}
            >
              Privacy Policy
            </Link>
            <Link
              className="text-[color:var(--Main-colour,#195AFF)] font-LatinkaMedium text-[14px] not-italic font-normal leading-[120%] !underline"
              href={'/terms-and-condition'}
            >
              Terms & Conditions
            </Link>
            <Link
              className="text-[color:var(--Main-colour,#195AFF)] font-LatinkaMedium text-[14px] not-italic font-normal leading-[120%] !underline"
              href={'/cookie-policy'}
            >
              Cookie Policy
            </Link>
          </div>
        </div>

        <div className='bg-[#01001E] py-[60px] md:py-[120px] flex items-center justify-center'>
          <div>
            <h3 className="text-white font-LatinkaBold text-center text-[32px] md:text-[48px] not-italic font-normal leading-[120%]">
              Always <span className="text-transparent bg-[linear-gradient(90deg,_#15BDFF_20.57%,_#C05DF2_54.45%)] bg-clip-text font-LatinkaBold text-[32px] md:text-[48px] not-italic font-normal leading-[120%]">
                On Schedule
              </span>{' '}
              always <span className="text-transparent mb-[16px] bg-[linear-gradient(90deg,_#15BDFF_20.57%,_#C05DF2_54.45%)] bg-clip-text font-LatinkaBold text-[32px] md:text-[48px] not-italic font-normal leading-[120%]">
                Reliable
              </span>
            </h3>

            <p className="text-[#DADAE4] text-center mb-[24px] font-LatinkaRegular text-[16px] md:text-[18px] not-italic font-normal leading-[150%]">
              Our team ensures timely results without compromising quality
            </p>

            <Link
              className="rounded-[8px] w-fit bg-[var(--Main-colour,#195AFF)] mx-auto flex h-[48px] px-[32px] py-[16px] justify-center items-center text-white font-LatinkaMedium text-[18px] not-italic font-normal leading-[120%]"
              href={'/book-demo'}
            >
              Book Free Demo
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default RefundPolicy;